import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ActionIcon, Menu, Skeleton } from '@mantine/core';
import { IconDots, IconTrash } from '@tabler/icons-react';
import BulletinSection from '@/components/common/BulletinSection';
import touch from '@/components/common/touchTarget.module.css';
import { useInView } from '@/hooks/useInView';
import { usePurchasePriceList } from '@/hooks/useBuy';
import { priceRangeOf, type BasisGroup } from '@/lib/buyTree';
import { makeGroupUrl } from '@/lib/buyLinks';
import { formatMoney, formatOrgName } from '@/lib/format';
import type { PriceListGroup } from '@/types/buy';
import classes from './BuyBasisSection.module.css';

interface BuyBasisSectionProps {
  readonly basis: BasisGroup;
  /** Удалить все прайс-листы базиса (подтверждение — на стороне вызова); нет — меню не показывается. */
  readonly onDelete?: () => void;
  /** Идёт удаление этого базиса: раздел приглушён и недоступен ни мышью, ни с клавиатуры. */
  readonly deleting?: boolean;
  /** Идёт удаление другого базиса: меню неактивно, второе удаление не запускается. */
  readonly menuDisabled?: boolean;
}

/** Диапазон моих цен прайс-листа. Запрос ленивый — когда строка попала на экран; цены нет или сбой — «—». */
function PriceRange({ group }: { readonly group: PriceListGroup }) {
  const { ref, inView } = useInView<HTMLSpanElement>();
  const { data, isLoading, isError } = usePurchasePriceList(
    inView ? { address: group.address, addressTitle: group.addressTitle, goods: group.goods } : null,
  );
  if (!inView || isLoading) {
    return (
      <span ref={ref} className={classes.skeleton}>
        <Skeleton h={14} w={110} />
      </span>
    );
  }
  const range = !isError && data ? priceRangeOf(data) : null;
  if (!range) {
    return <span>—</span>;
  }
  return <span>{range.min === range.max ? formatMoney(range.min) : `${formatMoney(range.min)}–${formatMoney(range.max)}`}</span>;
}

// Раздел базиса «Купить» («Бюллетень», как «Продать»): заголовок с зелёной линией — точка выгрузки, справа число
// прайс-листов и меню удаления всех прайс-листов базиса, под линией адрес. Прайс-листы — одной таблицей: строка —
// культура и год урожая, ведёт на деталь прайс-листа. На телефоне строка — блок с подписанными значениями.
export default function BuyBasisSection({ basis, onDelete, deleting = false, menuDisabled = false }: BuyBasisSectionProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const title = formatOrgName(basis.addressTitle) || basis.address;

  return (
    <div aria-busy={deleting || undefined} inert={deleting || undefined} className={deleting ? classes.deleting : undefined}>
      <BulletinSection
        title={title}
        aside={
          <span className={classes.aside}>
            <span className={classes.count}>{t('app.buy.priceLists', { count: basis.items.length })}</span>
            {onDelete ? (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    variant="default"
                    size={32}
                    className={touch.target}
                    aria-label={t('app.buy.del.basisMenu', { basis: title })}
                    loading={deleting}
                    disabled={deleting || menuDisabled}
                  >
                    <IconDots size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item color="red" leftSection={<IconTrash size={16} />} onClick={onDelete}>
                    {t('app.buy.del.basisButton')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ) : null}
          </span>
        }
      >
        {basis.address && basis.address !== title ? <p className={classes.address}>{basis.address}</p> : null}

        <table className={classes.table}>
          <colgroup>
            <col />
            <col className={classes.colUpdated} />
            <col className={classes.colPrice} />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">{t('app.buy.table.goods')}</th>
              <th scope="col">{t('app.buy.table.updated')}</th>
              <th scope="col" className={classes.num}>
                {t('app.buy.table.prices')}
              </th>
            </tr>
          </thead>
          <tbody>
            {basis.items.map((group) => (
              <tr key={group.goods} className={classes.row}>
                <td className={classes.goods}>
                  {/* Прежний вид списка (с фильтром) уходит в состояние перехода: «К списку» на детали вернёт к нему. */}
                  <Link to={makeGroupUrl(group)} state={{ listSearch: location.search }} className={classes.link}>
                    {group.goods}
                  </Link>
                </td>
                <td className={group.updatedDate ? classes.nowrap : classes.blank} data-label={t('app.buy.table.updated')}>
                  {group.updatedDate}
                </td>
                <td className={`${classes.num} ${classes.price}`} data-label={t('app.buy.table.prices')}>
                  <PriceRange group={group} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </BulletinSection>
    </div>
  );
}
